"use client";

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  X,
  FilePlus2,
  Building2,
  Hash,
  Store,
  UserRound,
  Briefcase,
  Mail,
  Phone,
  Smartphone,
  Layers,
  Boxes,
  Check,
  Send,
} from "lucide-react";
import { InvestmentDetailModal, type SavedInvestmentDetail } from "./InvestmentDetailModal";

const esferaOptions = [
  { value: "produto", label: "Produto" },
  { value: "valor", label: "Valor" },
];

const unidadeOptions = [
  { value: "prescricao", label: "Prescrição" },
  { value: "mip", label: "MIP" },
  { value: "genericos", label: "Genéricos" },
  { value: "dermatologia", label: "Dermatologia" },
];

const farmacias = [
  {
    nome: "Drogaria do João",
    cnpjMatriz: "19.105.575/0001-05",
    filiais: ["19.105.575/0002-86", "19.105.575/0003-67", "19.105.575/0004-48"],
    responsavel: "Carlos Eduardo Silva",
    cargo: "Gerente Comercial",
    email: "carlos.silva@drogariajoaofict.com.br",
    telefone: "(11) 3045-2200",
    celular: "(11) 98765-4321",
  },
  {
    nome: "Farmácia do José",
    cnpjMatriz: "61.412.110/0001-00",
    filiais: ["61.412.110/0015-22", "61.412.110/0032-10", "61.412.110/0048-55"],
    responsavel: "Mariana Costa Oliveira",
    cargo: "Diretora de Compras",
    email: "mariana.costa@farmaciajosefict.com.br",
    telefone: "(11) 3170-5500",
    celular: "(11) 99123-4567",
  },
  {
    nome: "Farmácia Boa Saúde",
    cnpjMatriz: "61.585.865/0001-51",
    filiais: ["61.585.865/0078-12", "61.585.865/0125-44", "61.585.865/0203-90"],
    responsavel: "Ricardo Almeida Souza",
    cargo: "Coordenador de Marketing",
    email: "ricardo.almeida@boasaudefict.com.br",
    telefone: "(11) 3265-7700",
    celular: "(11) 97654-3210",
  },
  {
    nome: "Drogaria Bem Estar",
    cnpjMatriz: "06.626.253/0001-51",
    filiais: ["06.626.253/0044-30", "06.626.253/0098-17", "06.626.253/0156-82"],
    responsavel: "Fernanda Lima Pereira",
    cargo: "Analista Sênior",
    email: "fernanda.lima@bemestarfict.com.br",
    telefone: "(85) 3255-8800",
    celular: "(85) 98877-6655",
  },
  {
    nome: "Farmácia Popular do Bairro",
    cnpjMatriz: "89.237.911/0001-40",
    filiais: ["89.237.911/0023-18", "89.237.911/0067-25", "89.237.911/0112-77"],
    responsavel: "André Schmidt Müller",
    cargo: "Gerente de Negociação",
    email: "andre.schmidt@popularbairrofict.com",
    telefone: "(51) 3216-4400",
    celular: "(51) 99876-5432",
  },
];

function ClearButton({ onClick, visible }: { onClick: () => void; visible: boolean }) {
  if (!visible) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
      aria-label="Limpar"
    >
      <X className="h-4 w-4" />
    </button>
  );
}

function OptionGroup({
  icon: Icon,
  label,
  value,
  options,
  onChange,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: { value: string; label: string } | null;
  options: { value: string; label: string }[];
  onChange: (value: string, label: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
        <Icon className="h-4 w-4 text-tudo-blue/70" />
        {label}
      </Label>
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
      >
        {options.map((opt) => {
          const active = value?.value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value, opt.label)}
              aria-pressed={active}
              className={`inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-tudo-blue focus:ring-offset-1 ${
                active
                  ? "border-tudo-blue bg-tudo-blue text-white shadow-sm"
                  : "border-border bg-background text-foreground hover:border-tudo-blue hover:text-tudo-blue"
              }`}
            >
              {active && <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={3} />}
              <span className="truncate">{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface InvestmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (data: SavedInvestmentDetail) => void;
}

function FieldRow({
  label,
  children,
  required = false,
  icon: Icon,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
        {Icon && <Icon className="h-4 w-4 text-tudo-blue/70" />}
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </Label>
      <div className="flex-1">{children}</div>
    </div>
  );
}

export function InvestmentModal({ open, onOpenChange, onSave }: InvestmentModalProps) {
  const [farmaciaIdx, setFarmaciaIdx] = React.useState(0);
  const farmacia = farmacias[farmaciaIdx];
  const [filial, setFilial] = React.useState("");
  const [responsavel, setResponsavel] = React.useState(farmacia.responsavel);
  const [cargo, setCargo] = React.useState(farmacia.cargo);
  const [email, setEmail] = React.useState(farmacia.email);
  const [telefone, setTelefone] = React.useState(farmacia.telefone);
  const [celular, setCelular] = React.useState(farmacia.celular);

  React.useEffect(() => {
    const f = farmacias[farmaciaIdx];
    setFilial("");
    setResponsavel(f.responsavel);
    setCargo(f.cargo);
    setEmail(f.email);
    setTelefone(f.telefone);
    setCelular(f.celular);
  }, [farmaciaIdx]);

  const [esfera, setEsfera] = React.useState<{ value: string; label: string } | null>(null);
  const [unidade, setUnidade] = React.useState<{ value: string; label: string } | null>(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const guardCloseRef = React.useRef(false);
  const prevDetailOpenRef = React.useRef(false);

  React.useEffect(() => {
    if (prevDetailOpenRef.current && !detailOpen) {
      guardCloseRef.current = true;
      const t = window.setTimeout(() => {
        guardCloseRef.current = false;
      }, 350);
      prevDetailOpenRef.current = detailOpen;
      return () => window.clearTimeout(t);
    }
    prevDetailOpenRef.current = detailOpen;
  }, [detailOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setDetailOpen(true);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => {
          if (detailOpen || guardCloseRef.current) e.preventDefault();
        }}
        onFocusOutside={(e) => {
          if (detailOpen || guardCloseRef.current) e.preventDefault();
        }}
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border-none bg-muted/30 p-0 shadow-2xl lg:max-w-4xl [&>button]:hidden"
      >
        {/* Header com gradiente */}
        <div className="relative overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-6 py-4">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                <FilePlus2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-left text-lg font-bold tracking-wide text-white">
                  Solicitar novo investimento
                </DialogTitle>
                <p className="text-xs font-medium text-white/70">
                  Preencha os dados do cliente e do investimento
                </p>
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-4 sm:p-5">
          {/* Dados do Cliente */}
          <section className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5">
            <div className="mb-4 flex items-center gap-2 border-b border-border pb-3">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-tudo-blue/10">
                <Building2 className="h-4 w-4 text-tudo-blue" />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wide text-tudo-blue">
                Dados do Cliente
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <FieldRow label="Nome do cliente:" icon={Store}>
                  <Select
                    value={String(farmaciaIdx)}
                    onValueChange={(v) => setFarmaciaIdx(Number(v))}
                  >
                    <SelectTrigger className="border-border bg-muted/40 text-sm text-foreground transition-colors focus:ring-tudo-blue">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {farmacias.map((f, i) => (
                        <SelectItem key={f.cnpjMatriz} value={String(i)}>
                          {f.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldRow>
              </div>
              <FieldRow label="CNPJ da Matriz:" icon={Hash}>
                <Input
                  readOnly
                  value={farmacia.cnpjMatriz}
                  className="border-border bg-muted/40 text-sm font-medium text-muted-foreground"
                />
              </FieldRow>
              <FieldRow label="CNPJ da Filial:" icon={Hash}>
                <div className="flex items-center gap-1">
                  <Select value={filial} onValueChange={setFilial}>
                    <SelectTrigger className="border-border text-sm focus:ring-tudo-blue">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {farmacia.filiais.map((f) => (
                        <SelectItem key={f} value={f}>
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <ClearButton onClick={() => setFilial("")} visible={!!filial} />
                </div>
              </FieldRow>
              <FieldRow label="Responsável:" icon={UserRound}>
                <Input
                  value={responsavel}
                  onChange={(e) => setResponsavel(e.target.value)}
                  className="border-border text-sm focus-visible:ring-tudo-blue"
                />
              </FieldRow>
              <FieldRow label="Cargo:" icon={Briefcase}>
                <Input
                  value={cargo}
                  onChange={(e) => setCargo(e.target.value)}
                  className="border-border text-sm focus-visible:ring-tudo-blue"
                />
              </FieldRow>
              <FieldRow label="E-mail:" icon={Mail}>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border-border text-sm focus-visible:ring-tudo-blue"
                />
              </FieldRow>
              <FieldRow label="Telefone:" icon={Phone}>
                <Input
                  type="tel"
                  value={telefone}
                  onChange={(e) => setTelefone(e.target.value)}
                  className="border-border text-sm focus-visible:ring-tudo-blue"
                />
              </FieldRow>
              <FieldRow label="Celular:" icon={Smartphone}>
                <Input
                  type="tel"
                  value={celular}
                  onChange={(e) => setCelular(e.target.value)}
                  className="border-border text-sm focus-visible:ring-tudo-blue"
                />
              </FieldRow>
            </div>
          </section>

          {/* Dados do Investimento */}
          <section className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5">
            <div className="mb-4 flex items-center gap-2 border-b border-border pb-3">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-tudo-green/10">
                <Layers className="h-4 w-4 text-tudo-green" />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wide text-tudo-blue">
                Dados do Investimento
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-[1fr_2fr]">
              <OptionGroup
                icon={Layers}
                label="Natureza"
                value={esfera}
                options={esferaOptions}
                onChange={(value, label) => setEsfera({ value, label })}
              />
              <OptionGroup
                icon={Boxes}
                label="Unidade de negócios"
                value={unidade}
                options={unidadeOptions}
                onChange={(value, label) => setUnidade({ value, label })}
              />
            </div>
          </section>

          {/* Botão Solicitar */}
          <div className="flex justify-center sm:justify-end">
            <button
              type="submit"
              className="group flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-tudo-blue to-tudo-blue/85 px-10 py-3.5 text-sm font-semibold uppercase tracking-wide text-white shadow-lg shadow-tudo-blue/20 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-tudo-blue/30 focus:outline-none focus:ring-2 focus:ring-tudo-blue focus:ring-offset-2 sm:w-auto"
            >
              <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              Solicitar investimento
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>

    <InvestmentDetailModal
      open={detailOpen}
      onOpenChange={setDetailOpen}
      unidadeNegocios={unidade?.label ?? "—"}
      natureza={esfera?.label ?? ""}
      onSave={(data) => {
        onSave?.(data);
        onOpenChange(false);
      }}
    />
    </>
  );
}
